import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, Share2, Copy, Check } from "lucide-react";
import api, { resolveMediaUrl } from "@/lib/api";
import { toast } from "sonner";

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const parallaxY = useTransform(heroProgress, [0, 1], [0, 160]);
  const parallaxScale = useTransform(heroProgress, [0, 1], [1, 1.08]);

  // Reading progress
  const { scrollYProgress } = useScroll();
  const progressWidth = useSpring(scrollYProgress, { stiffness: 120, damping: 25, mass: 0.4 });

  useEffect(() => {
    setBlog(null);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await api.get(`/blogs/${slug}`);
        setBlog(data.blog);
        setRelated(data.related || []);
        // SEO
        if (data.blog?.seo_title || data.blog?.title) {
          document.title = `${data.blog.seo_title || data.blog.title} — Novarise Works`;
        }
        const desc = data.blog?.seo_description || data.blog?.excerpt;
        if (desc) {
          let meta = document.querySelector('meta[name="description"]');
          if (meta) meta.setAttribute("content", desc);
        }
      } catch {
        setNotFound(true);
      }
    })();
    return () => {
      document.title = "Novarise Works — Growth Infrastructure for Ambitious Businesses";
    };
  }, [slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  if (notFound) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-center px-6 pt-32">
        <div>
          <p className="overline mb-3">404</p>
          <h1 className="text-4xl font-extrabold tracking-tight">Post not found</h1>
          <button onClick={() => navigate("/blog")} className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-white text-bg-0 font-semibold">
            <ArrowLeft size={16} /> Back to journal
          </button>
        </div>
      </div>
    );
  }

  if (!blog) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 pt-32">Loading…</div>;
  }

  const dateStr = blog.published_at
    ? new Date(blog.published_at).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <div data-testid="blog-detail-root" className="relative">
      {/* Reading progress */}
      <motion.div
        style={{ scaleX: progressWidth, transformOrigin: "0% 50%" }}
        className="fixed top-0 left-0 right-0 h-[3px] z-[60] bg-gradient-to-r from-neon-orange via-neon-magenta to-neon-cyan"
      />

      {/* HERO with parallax featured image */}
      <section ref={heroRef} className="relative pt-32 pb-14 sm:pt-40 sm:pb-16 overflow-hidden">
        <div className="container-x">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white transition mb-8">
            <ArrowLeft size={14} /> The Novarise Journal
          </Link>

          <div className="flex flex-wrap items-center gap-2 mb-6">
            {(blog.categories || []).map((c) => (
              <span key={c} className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/55 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
                {c}
              </span>
            ))}
          </div>

          {/* Masked line reveal on title */}
          <h1 className="text-3xl sm:text-5xl lg:text-6xl leading-[1.02] font-extrabold tracking-tight max-w-4xl">
            <span className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="block"
              >
                {blog.title}
              </motion.span>
            </span>
          </h1>

          {blog.excerpt && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed"
            >
              {blog.excerpt}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-8 flex flex-wrap items-center gap-4 text-xs text-white/50 font-mono uppercase tracking-[0.14em]"
          >
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={12} /> {dateStr}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} /> {blog.reading_time} min read
            </span>
            <span className="hidden sm:inline text-white/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              By <span className="text-white/80 font-semibold normal-case tracking-normal">{blog.author_name || "Novarise Works"}</span>
            </span>
            <button
              onClick={copyLink}
              className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition normal-case tracking-normal"
              data-testid="share-btn"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy link"}
            </button>
          </motion.div>
        </div>

        {/* Featured image with parallax */}
        {blog.featured_image && (
          <div className="mt-10 sm:mt-14 relative">
            <div className="container-x">
              <div className="relative aspect-[16/9] rounded-3xl overflow-hidden border border-white/[0.06]">
                <motion.img
                  src={resolveMediaUrl(blog.featured_image)}
                  alt={blog.featured_image_alt || blog.title}
                  style={{ y: parallaxY, scale: parallaxScale }}
                  className="absolute inset-0 w-full h-[115%] object-cover will-change-transform"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-0/40 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* CONTENT */}
      <section className="section pt-8 pb-16">
        <div className="container-x">
          <motion.article
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="blog-body"
            className="prose prose-invert prose-lg max-w-3xl mx-auto"
            dangerouslySetInnerHTML={{ __html: blog.body_html || "" }}
          />

          {/* Tags + share */}
          <div className="max-w-3xl mx-auto mt-14 pt-8 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {(blog.tags || []).map((t) => (
                <Link
                  key={t}
                  to={`/blog?tag=${encodeURIComponent(t)}`}
                  className="text-[10px] font-mono uppercase tracking-[0.16em] text-white/50 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 hover:text-white"
                >
                  #{t}
                </Link>
              ))}
            </div>
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition"
            >
              <Share2 size={14} /> Share
            </button>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="section pt-6 pb-24 relative border-t border-white/[0.06]">
          <div className="container-x">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="overline mb-3">Keep reading</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">More from the journal</h2>
              </div>
              <Link to="/blog" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1">
                All posts <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((r, i) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="group"
                >
                  <Link to={`/blog/${r.slug}`} className="block">
                    <div className="relative aspect-[16/11] rounded-2xl overflow-hidden border border-white/[0.06] bg-bg-2">
                      {r.featured_image ? (
                        <img
                          src={resolveMediaUrl(r.featured_image)}
                          alt={r.featured_image_alt || r.title}
                          className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-[1.04]"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-neon-cyan/30 via-neon-purple/30 to-neon-magenta/30" />
                      )}
                    </div>
                    <h3 className="mt-4 font-bold tracking-tight text-lg group-hover:text-gradient-neon transition-all">{r.title}</h3>
                    {r.excerpt && <p className="mt-2 text-sm text-white/55 line-clamp-2">{r.excerpt}</p>}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
